import React from 'react';
import { Instagram, Facebook, Youtube, Linkedin, ArrowUp, Mail, Phone, MapPin, Globe } from 'lucide-react';
import { navLinks, contactInfo, socialLinks } from '../mock';
import { Link } from 'react-router-dom';

const iconMap = { instagram: Instagram, facebook: Facebook, youtube: Youtube, linkedin: Linkedin };

const Footer = () => {
  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="bg-[#0a0a0a] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src="/sgp-logo.png"
              alt="Simply Gold Productions logo"
              width="1200"
              height="630"
              className="h-24 w-auto object-contain logo-blend -ml-2"
            />
            <p className="text-white/45 text-sm mt-4 max-w-xs">
              Bringing imagination to screen &mdash; corporate, sports and documentary
              storytelling from Kelowna.
            </p>
          </div>

          {/* Explore */}
          <div>
            <h4 className="font-display font-semibold text-[#c9a85f] text-lg mb-5 tracking-wide">Explore</h4>
            <nav className="flex flex-col gap-3">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => { e.preventDefault(); document.querySelector(l.href)?.scrollIntoView({ behavior: 'smooth' }); }}
                  className="text-left text-white/60 hover:text-[#c9a85f] text-sm font-medium transition-colors w-fit"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="text-left text-white/60 hover:text-[#c9a85f] text-sm font-medium transition-colors w-fit"
              >
                Contact
              </a>
            </nav>
          </div>

          {/* Contact Info (SEO) */}
          <address className="not-italic">
            <h4 className="font-display font-semibold text-[#c9a85f] text-lg mb-5 tracking-wide">Contact Info</h4>
            <p className="text-white/70 text-sm font-medium">{contactInfo.company}</p>
            <p className="text-white/45 text-sm mb-4">{contactInfo.tagline}</p>
            <ul className="space-y-3">
              <li>
                <a href={`mailto:${contactInfo.email}`} className="group flex items-center gap-3 text-white/60 hover:text-[#c9a85f] text-sm transition-colors">
                  <Mail size={16} className="text-[#c9a85f] shrink-0" />
                  <span className="whitespace-nowrap">{contactInfo.email}</span>
                </a>
              </li>
              <li>
                <a href={`tel:${contactInfo.phoneHref}`} className="flex items-center gap-3 text-white/60 hover:text-[#c9a85f] text-sm transition-colors">
                  <Phone size={16} className="text-[#c9a85f] shrink-0" />
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/60 text-sm">
                <MapPin size={16} className="text-[#c9a85f] mt-0.5 shrink-0" />
                <span>{contactInfo.addressLine1}<br />{contactInfo.addressLine2}</span>
              </li>
              <li>
                <a href={contactInfo.url} className="flex items-center gap-3 text-white/60 hover:text-[#c9a85f] text-sm transition-colors">
                  <Globe size={16} className="text-[#c9a85f] shrink-0" />
                  simplygoldproductions.com
                </a>
              </li>
            </ul>
          </address>

          {/* Social */}
          <div>
            <h4 className="font-display font-semibold text-[#c9a85f] text-lg mb-5 tracking-wide">Social</h4>
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((s) => {
                const Icon = iconMap[s.icon];
                return (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    title={s.name}
                    className="w-11 h-11 rounded-full border border-white/15 text-white/70 flex items-center justify-center hover:bg-[#c9a85f] hover:text-black hover:border-[#c9a85f] transition-colors"
                  >
                    <Icon size={19} />
                  </a>
                );
              })}
            </div>
            <a
              href={contactInfo.calendly}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-6 text-sm font-display font-semibold text-black bg-[#c9a85f] hover:bg-[#d8b970] px-5 py-2.5 rounded-md transition-colors"
            >
              Book A Meeting
            </a>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-14 pt-8 border-t border-white/10">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-white/40">
            <p>&copy; {new Date().getFullYear()} Simply Gold Productions. All rights reserved.</p>
            <Link to="/privacy" className="hover:text-[#c9a85f]">Privacy notice</Link>
            <button onClick={() => window.dispatchEvent(new Event('open-cookie-settings'))} className="hover:text-[#c9a85f]">Privacy choices</button>
          </div>
          <button
            onClick={scrollTop}
            className="group inline-flex items-center gap-2 text-white/50 hover:text-[#c9a85f] text-xs font-display tracking-widest uppercase transition-colors"
          >
            Back To Top
            <ArrowUp size={14} className="group-hover:-translate-y-1 transition-transform" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
